import { Box, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'next-i18next';
import Image from 'src/components/atoms/image';
import config from 'src/config';
import { useBreakpointV2 } from 'src/helpers/use';

import CourseCardCertificateRibbon from './bundleCourseCardCertificate';
import BundleCourseCardInstructor from './bundleCourseCardInstructor';
import { BundleCourseCardStyle } from './style';

import FontAwesomeIcon from '@components/atoms/fontAwesomeIcon';
import { BundleCourseInstructorEntity } from '@redux/bundle/type/BundleDetailType';

type Props = {
  courseNo: number;
  courseImage: string;
  courseName: string;
  isEnrolled: boolean;
  hasCertificate: boolean;
  countdownDay: number;
  instructors: BundleCourseInstructorEntity[];
};

const BundleCourseCard = ({
  courseNo,
  courseImage,
  courseName,
  isEnrolled,
  hasCertificate,
  countdownDay,
  instructors,
}: Props) => {
  const theme = useTheme();
  const { isMobile } = useBreakpointV2();
  const { t } = useTranslation('store_course');

  return (
    <BundleCourseCardStyle>
      <Box className="course-card-detail-box">
        <Box className="course-card-image-box">
          <Box className="course-card-no-box">
            <Typography variant="h6" color={theme.palette.textV2.secondary}>
              {t('bundle_detail.bundle_course_items.index.title')}
            </Typography>
            <Typography
              lineHeight="43px"
              fontSize="64px"
              fontWeight={400}
              fontStyle="normal"
              color={theme.palette.textV2.secondary}
            >
              {courseNo}
            </Typography>
          </Box>
          <Image
            className="image"
            style={{ borderRadius: '10px' }}
            src={courseImage}
            errorImage={`${config.IMAGE_CDN_HOST}/assets/thumbnail-course-card.jpg`}
            alt={courseName}
          />
        </Box>
        <Box className="course-card-info-box">
          <Typography
            variant={isMobile ? 'MobileMiniDescription' : 'BaseLegacy'}
            color={theme.palette.textV2.secondary}
          >
            <span>{t('bundle_detail.bundle_course_items.content.card.general_learn')}</span>
            {countdownDay
              ? t('bundle_detail.bundle_course_items.content.card.countdownDay.limit', { day: countdownDay })
              : t('bundle_detail.bundle_course_items.content.card.countdownDay.lifetime')}
          </Typography>
          <Typography
            variant="h5"
            className="course-title"
            color={theme.palette.textV2.primary}
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {courseName}
          </Typography>
          <Box className="course-card-instructors-info-box">
            {instructors[0] && (
              <BundleCourseCardInstructor fullName={instructors[0].fullName} imageUrl={instructors[0].imageUrl} />
            )}
            {instructors[1] && !isMobile && (
              <BundleCourseCardInstructor fullName={instructors[1].fullName} imageUrl={instructors[1].imageUrl} />
            )}
          </Box>
          {isEnrolled && (
            <Box className="course-card-enrolled-box">
              <Box
                color={isMobile ? theme.palette.success.main : theme.palette.textOnPrimary.primary}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '3px',
                }}
              >
                <FontAwesomeIcon icon="fa-light fa-circle-check" style={{ fontSize: '16px' }} />
                <Typography variant="tagTextBold" className="enrolled-title">
                  {t('bundle_detail.bundle_course_items.content.card.enrolled')}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
      <Box className="ribbon-certificate-box">{hasCertificate && <CourseCardCertificateRibbon />}</Box>
    </BundleCourseCardStyle>
  );
};

export default BundleCourseCard;
